import React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Chip,
  Divider,
  Button,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert as MUIAlert,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";


/** If you have a UserContext and want to gate provider actions, you can use it */
import { UserContext } from "../../../../platform/components/auth/auth-context";
import {
  PROVIDER_RESPOND_INVITE,
  PICK_BROADCAST_WINNER,
  SEND_CONCIERGE_BROADCAST_INVITES,
  ADD_BUSINESSES_TO_CONCIERGE_BROADCAST_GROUP,
  RESEND_CONCIERGE_BROADCAST_INVITE,
} from "../../../../platform/graphql/mutations/care";
import {
  GET_REFERRAL_BROADCAST_GROUP,
  SEARCH_BROADCAST_BUSINESSES,
} from "../../../../platform/graphql/queries/care";
import { GET_PLATFORM_ADMIN_BUSINESSES } from "../../careComponents/pages/planyAdminDashboard/query";

type InviteStatus = "DRAFT" | "SENT" | "ACCEPTED" | "DECLINED" | "EXPIRED" | "NO_RESPONSE";

const statusColor: Record<InviteStatus, "default" | "success" | "warning" | "error" | "info"> = {
  DRAFT: "default",
  SENT: "info",
  ACCEPTED: "success",
  DECLINED: "error",
  EXPIRED: "warning",
  NO_RESPONSE: "default",
};

function prettyDateLabel(v?: string | null) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime())
    ? null
    : d
        .toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        })
        .replace(/\s?(AM|PM)\b/g, (m) => m.toLowerCase());
}

function formatBusinessAddress(business: any) {
  if (!business) return null;
  const parts = [
    String(business?.address || business?.street || "").trim(),
    String(business?.cityName || "").trim(),
    String(business?.stateCode || "").trim(),
    String(business?.zipcode || "").trim(),
  ].filter(Boolean);
  if (!parts.length) return null;

  const [street, city, state, zip] = parts;
  const locality = [city, state, zip].filter(Boolean).join(", ").replace(", ,", ",");

  return [street, locality].filter(Boolean).join(" • ");
}

function parseSeed(seedJson: any) {
  if (!seedJson) return {};
  if (typeof seedJson === "object") return seedJson;
  try {
    return JSON.parse(String(seedJson));
  } catch {
    return {};
  }
}

const CLAIMED_STATUSES = new Set(["CLAIMED", "VERIFIED", "APPROVED", "OWNED"]);

function normalizeZip5(value: any) {
  const match = String(value || "").match(/\d{5}/);
  return match ? match[0] : "";
}

function isClaimedBusiness(business: any) {
  return CLAIMED_STATUSES.has(String(business?.claimStatus || "").trim().toUpperCase());
}

function compareBusinessSearchRank(left: any, right: any, preferredZip: string) {
  const leftZipRank = preferredZip && normalizeZip5(left?.zipcode) === preferredZip ? 0 : 1;
  const rightZipRank = preferredZip && normalizeZip5(right?.zipcode) === preferredZip ? 0 : 1;
  if (leftZipRank !== rightZipRank) return leftZipRank - rightZipRank;

  const leftActiveRank = Number(left?.status) === 1 ? 0 : 1;
  const rightActiveRank = Number(right?.status) === 1 ? 0 : 1;
  if (leftActiveRank !== rightActiveRank) return leftActiveRank - rightActiveRank;

  const leftClaimedRank = isClaimedBusiness(left) ? 0 : 1;
  const rightClaimedRank = isClaimedBusiness(right) ? 0 : 1;
  if (leftClaimedRank !== rightClaimedRank) return leftClaimedRank - rightClaimedRank;

  return String(left?.name || "").localeCompare(String(right?.name || ""));
}

function getMatchScore(item: any, query: string) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return 0;

  const name = String(item?.name || "").toLowerCase();
  const city = String(item?.cityName || "").toLowerCase();
  const state = String(item?.stateCode || "").toLowerCase();
  const zip = String(item?.zipcode || "").toLowerCase();
  const url = String(item?.url || "").toLowerCase();
  const text = `${name} ${city} ${state} ${zip} ${url}`;

  let score = 0;
  if (name === q) score += 140;
  else if (name.startsWith(q)) score += 105;
  else if (name.includes(q)) score += 80;

  if (city === q) score += 90;
  else if (city.startsWith(q)) score += 70;
  else if (city.includes(q)) score += 50;

  if (state === q) score += 70;
  if (zip === q) score += 80;
  else if (zip.startsWith(q)) score += 45;

  if (url === q) score += 75;
  else if (url.startsWith(q)) score += 55;
  else if (url.includes(q)) score += 35;

  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length > 1) {
    score += tokens.filter((token) => text.includes(token)).length * 14;
  }

  if (text.includes(q)) score += 20;
  return score;
}

function isZipQuery(value: string) {
  return /^\d{5}$/.test(String(value || "").trim());
}

function getInviteStatusLabel(status: InviteStatus, residentReference?: string | null) {
  const residentSuffix = residentReference
    ? ` • ${residentReference}`
    : "";
  switch (String(status || "").toUpperCase()) {
    case "DRAFT":
      return `Added${residentSuffix}`;
    case "SENT":
      return `Sent for This Resident${residentSuffix}`;
    case "NO_RESPONSE":
      return `Waiting for Response${residentSuffix}`;
    case "ACCEPTED":
      return `Accepted${residentSuffix}`;
    case "DECLINED":
      return `Declined${residentSuffix}`;
    case "EXPIRED":
      return `Expired${residentSuffix}`;
    default:
      return `${status}${residentSuffix}`;
  }
}

/* Small dialog to collect an optional provider note when responding */
function RespondDialog({
  open,
  onClose,
  onSubmit,
  initialNote = "",
  title = "Respond to Invite",
  actionLabel = "Submit",
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (note: string) => void;
  initialNote?: string;
  title?: string;
  actionLabel?: string;
}) {
  const [note, setNote] = React.useState(initialNote);
  React.useEffect(() => {
    if (open) setNote(initialNote || "");
  }, [open, initialNote]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          label="Optional note for referrer"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          placeholder="Add context (availability, timing, requirements, etc.)"
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(note)}>{actionLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ReferralBroadcastGroupPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { providerUser } = React.useContext(UserContext);

  const { data, loading, error, refetch } = useQuery(GET_REFERRAL_BROADCAST_GROUP, {
    variables: { id: groupId },
    skip: !groupId,
    fetchPolicy: "cache-and-network",
  });

  const [respondInvite, { loading: responding }] = useMutation(PROVIDER_RESPOND_INVITE, {
    onCompleted: () => {
      refetch?.();
    },
  });

  const [pickWinner, { loading: picking }] = useMutation(PICK_BROADCAST_WINNER, {
    onCompleted: () => {
      refetch?.();
    },
  });
  const [sendDraftInvites, { loading: sendingDraftInvites }] = useMutation(
    SEND_CONCIERGE_BROADCAST_INVITES,
    {
      onCompleted: () => {
        refetch?.();
      },
    }
  );
  const [addBusinessesToGroup, { loading: addingBusinesses }] = useMutation(
    ADD_BUSINESSES_TO_CONCIERGE_BROADCAST_GROUP,
    {
      onCompleted: () => {
        refetch?.();
      },
    }
  );
  const [resendInvite, { loading: resendingInvite }] = useMutation(
    RESEND_CONCIERGE_BROADCAST_INVITE,
    {
      onCompleted: () => {
        refetch?.();
      },
    }
  );

  const [snack, setSnack] = React.useState<string | null>(null);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchActionError, setSearchActionError] = React.useState<string | null>(null);
  const [respondState, setRespondState] = React.useState<{
    inviteId: string | null;
    action: InviteStatus | null;
  }>({ inviteId: null, action: null });

  const trimmedSearchQuery = searchQuery.trim();
  const searchIsZip = isZipQuery(trimmedSearchQuery);
  const [fallbackSearchBusinesses, { loading: searchingFallbackBusinesses }] =
    useLazyQuery(SEARCH_BROADCAST_BUSINESSES, {
      fetchPolicy: "network-only",
    });
  const [fallbackSearchItems, setFallbackSearchItems] = React.useState<any[]>([]);

  const { data: searchData, loading: searchingBusinesses, error: searchError } = useQuery(
    GET_PLATFORM_ADMIN_BUSINESSES,
    {
      variables: {
        countryId: null,
        stateCode: null,
        search: searchIsZip ? null : trimmedSearchQuery || null,
        limit: 20,
        offset: 0,
        zip: searchIsZip ? trimmedSearchQuery : null,
        status: null,
        claimed: null,
      },
      skip: !searchOpen || trimmedSearchQuery.length < 2,
      fetchPolicy: "network-only",
    }
  );

  React.useEffect(() => {
    let ignore = false;

    if (!searchOpen || trimmedSearchQuery.length < 2 || searchIsZip) {
      setFallbackSearchItems([]);
      return () => {
        ignore = true;
      };
    }

    const primaryItems = searchData?.adminBusinessesPaged?.items || [];
    if (primaryItems.length > 0 || searchError) {
      setFallbackSearchItems([]);
      return () => {
        ignore = true;
      };
    }

    (async () => {
      try {
        const searchPayload = JSON.stringify({
          searchQuery: trimmedSearchQuery,
          locationQuery: "",
          dateQuery: null,
          countryId: null,
        });
        const result = await fallbackSearchBusinesses({
          variables: { query: searchPayload },
        });
        if (ignore) return;
        const items = Array.isArray(result?.data?.search) ? result.data.search : [];
        setFallbackSearchItems(items);
      } catch (_err) {
        if (!ignore) setFallbackSearchItems([]);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [
    fallbackSearchBusinesses,
    searchData?.adminBusinessesPaged?.items,
    searchError,
    searchIsZip,
    searchOpen,
    trimmedSearchQuery,
  ]);

  const closeSnack = () => setSnack(null);

  const group = data?.referralBroadcastGroup ?? null;
  const invites = React.useMemo(() => group?.invites ?? [], [group?.invites]);
  const seed = parseSeed(group?.seedJson);
  const residentReference =
    String(
      seed?.residentKey ||
      seed?.extra?.residentKey ||
      seed?.extra?.patientKey ||
      seed?.extra?.patientInitials ||
      ""
    ).trim() || null;
  const preferredZip = React.useMemo(() => {
    const seedExtra =
      seed?.extra && typeof seed.extra === "string"
        ? parseSeed(seed.extra)?.broadcastGeo || {}
        : seed?.extra?.broadcastGeo || {};
    return normalizeZip5(
      searchIsZip
        ? trimmedSearchQuery
        : seedExtra?.zip || seed?.zip || seed?.zipcode || ""
    );
  }, [searchIsZip, seed, trimmedSearchQuery]);
  const isPrivateGroup = String(seed?.payerType || "").toUpperCase() === "PRIVATE";
  const currentUserId = String(providerUser?.userId || "").trim();
  const assignedAgentId = String(group?.assignedAgentId || group?.ownerAgentId || "").trim();
  const canSendPrivateInvites =
    isPrivateGroup &&
    !!providerUser &&
    (
      providerUser?.isPlatformAdmin === true ||
      providerUser?.isDefaultConcierge === true ||
      providerUser?.isAgent === true
    ) &&
    (!!providerUser?.isPlatformAdmin || (assignedAgentId && currentUserId === assignedAgentId));

  const winnerInviteId = group?.winnerInviteId ?? null;
  const winnerLeadId = group?.winnerLeadId ?? null;
  const draftInvites = invites.filter((i: any) => i.status === "DRAFT");

  const acceptedInvites = invites.filter((i: any) => i.status === "ACCEPTED");
  const canPickWinner = !winnerInviteId && acceptedInvites.length > 0;
  const inviteBusinessIds = React.useMemo(
    () =>
      new Set(
        invites
          .map((invite: any) => Number(invite.businessId))
          .filter((value: number) => Number.isFinite(value))
      ),
    [invites]
  );
  const rankedSearchResults = React.useMemo(() => {
    const primaryRows = searchData?.adminBusinessesPaged?.items || [];
    const baseRows = primaryRows.length > 0 ? primaryRows : fallbackSearchItems;
    return [...baseRows]
      .map((item: any, index: number) => ({
        item,
        index,
        matchScore: getMatchScore(item, trimmedSearchQuery),
      }))
      .sort((left, right) => {
        if (left.matchScore !== right.matchScore) {
          return right.matchScore - left.matchScore;
        }
        const rankDiff = compareBusinessSearchRank(
          left.item,
          right.item,
          preferredZip
        );
        if (rankDiff !== 0) return rankDiff;
        return left.index - right.index;
      })
      .map((entry) => entry.item);
  }, [
    fallbackSearchItems,
    preferredZip,
    searchData?.adminBusinessesPaged?.items,
    trimmedSearchQuery,
  ]);
  const searchResults = React.useMemo(
    () =>
      rankedSearchResults.filter(
        (business: any) => !inviteBusinessIds.has(Number(business?.id))
      ),
    [inviteBusinessIds, rankedSearchResults]
  );
  const existingBatchSearchResults = React.useMemo(
    () =>
      rankedSearchResults.filter((business: any) =>
        inviteBusinessIds.has(Number(business?.id))
      ),
    [inviteBusinessIds, rankedSearchResults]
  );

  const onProviderRespond = (inviteId: string, action: "ACCEPTED" | "DECLINED") => {
    setRespondState({ inviteId, action });
  };

  const actuallyRespond = async (note: string) => {
    if (!respondState.inviteId || !respondState.action) return;
    try {
      await respondInvite({
        variables: {
          inviteId: respondState.inviteId,
          action: respondState.action,
          providerNote: note || null,
        },
      });
      setSnack(
        respondState.action === "ACCEPTED"
          ? "Invite accepted."
          : "Invite declined."
      );
    } catch (e: any) {
      setSnack(e?.message || "Failed to respond.");
    } finally {
      setRespondState({ inviteId: null, action: null });
    }
  };

  const onPickWinner = async (inviteId: string) => {
    try {
      await pickWinner({
        variables: { groupId, inviteId },
      });
      setSnack("Winner selected. A targeted referral has been created.");
    } catch (e: any) {
      setSnack(e?.message || "Failed to pick winner.");
    }
  };

  const onSendDraftInvite = async (businessId?: string | number | null) => {
    try {
      await sendDraftInvites({
        variables: {
          groupId,
          businessIds: businessId != null ? [String(businessId)] : undefined,
        },
      });
      setSnack(
        businessId != null
          ? "Provider request sent."
          : "Matched providers have been sent this private request."
      );
    } catch (e: any) {
      setSnack(e?.message || "Failed to send provider request.");
    }
  };

  const onAddBusinessToBatch = async (businessId: string | number, sendNow = false) => {
    setSearchActionError(null);
    try {
      await addBusinessesToGroup({
        variables: {
          groupId,
          businessIds: [String(businessId)],
        },
      });
      if (sendNow) {
        await sendDraftInvites({
          variables: {
            groupId,
            businessIds: [String(businessId)],
          },
        });
      }
      await refetch?.();
      setSnack(sendNow ? "Provider added to this batch and invited." : "Provider added to this batch.");
    } catch (e: any) {
      setSearchActionError(e?.message || "Failed to add provider to this batch.");
      setSnack(e?.message || "Failed to add provider to this batch.");
    }
  };

  const onResendInvite = async (inviteId: string) => {
    try {
      await resendInvite({
        variables: { inviteId },
      });
      setSnack("Invite resent to this provider.");
    } catch (e: any) {
      setSnack(e?.message || "Failed to resend invite.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to="/referrals/portal"
            >
              Back to Referral Portal
            </Button>
          }
        >
          {error.message}
        </Alert>
      </Container>
    );
  }
  if (!group) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to="/referrals/portal"
            >
              Back to Referral Portal
            </Button>
          }
        >
          Group not found.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Container sx={{ py: 4, maxWidth: "md" as any }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 2 }}>
          <Typography variant="h4" fontWeight={800}>Broadcast Group</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              component={RouterLink}
              to="/referrals/portal"
            >
              Referral Portal
            </Button>
            <Button variant="outlined" onClick={() => navigate("/care")}>Browse Providers</Button>
            <Button variant="outlined" onClick={() => refetch?.()}>Refresh</Button>
          </Stack>
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            mb: 2,
            bgcolor: (t) => alpha(t.palette.info.main, 0.06),
            borderColor: (t) => alpha(t.palette.info.main, 0.35),
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Group #{group.id}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`Invites: ${invites.length}`} size="small" />
              {draftInvites.length > 0 ? (
                <Chip label={`Draft ${draftInvites.length}`} size="small" variant="outlined" />
              ) : null}
              {winnerInviteId ? (
                <>
                  <Chip color="success" label="Winner Picked" size="small" />
                  {winnerLeadId && (
                    <Chip
                      color="success"
                      variant="outlined"
                      size="small"
                      label={`Lead #${winnerLeadId}`}
                      component={RouterLink}
                      to={`/care/referrals/${winnerLeadId}`}
                      clickable
                    />
                  )}
                </>
              ) : (
                <Chip color="warning" label="Awaiting Winner" size="small" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Created: {prettyDateLabel(group.createdAt) || "—"} • Updated: {prettyDateLabel(group.updatedAt) || "—"}
            </Typography>
            {residentReference ? (
              <Typography variant="body2">
                Reference: <strong>{residentReference}</strong>
              </Typography>
            ) : null}
            {isPrivateGroup ? (
              <Alert severity="info">
                Private-pay requests stay with the assigned agent first. Providers do not receive this request until the agent sends it.
              </Alert>
            ) : null}
            {canSendPrivateInvites ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SendIcon />}
                  disabled={sendingDraftInvites || draftInvites.length === 0}
                  onClick={() => onSendDraftInvite(null)}
                >
                  Send to All Unsent Providers
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={addingBusinesses || sendingDraftInvites}
                  onClick={() => setSearchOpen(true)}
                >
                  Add Provider To This Batch
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={700}>Invites</Typography>
          </Box>
          <Divider />
          <Box
            sx={{
              p: 2,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
              gap: 2,
            }}
          >
            {invites.length === 0 ? (
              <Alert severity="info">No invites in this group.</Alert>
            ) : (
              invites.map((inv: any) => {
                const business = inv.business;
                const businessEmail =
                  business?.emails?.[0] ?? null;
                const businessPhone =
                  business?.phoneNumbers?.[0]?.phoneNumber ?? null;
                const businessAddress = formatBusinessAddress(business);
                const respondedAt = prettyDateLabel(inv.respondedAt);
                const inviteSentAt = prettyDateLabel(inv.actionTokenExpiresAt)
                  ? prettyDateLabel(
                      new Date(
                        new Date(inv.actionTokenExpiresAt).getTime() - 72 * 60 * 60 * 1000
                      ).toISOString()
                    )
                  : null;
                const canProviderAct = !winnerInviteId && (inv.status === "SENT" || inv.status === "NO_RESPONSE");
                const canPickThis = canPickWinner && inv.status === "ACCEPTED";
                const canResendPrivateInvite =
                  canSendPrivateInvites &&
                  !winnerInviteId &&
                  ["NO_RESPONSE", "DECLINED", "EXPIRED"].includes(
                    String(inv.status || "").toUpperCase()
                  );

                return (
                  <Paper
                    key={inv.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 260,
                    }}
                  >
                    <Stack spacing={1.25} sx={{ flex: 1 }}>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                        justifyContent="space-between"
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 1 }}>
                              {business?.name ?? "Provider"}
                            </Typography>
                            {business?.url && (
                              <Chip
                                size="small"
                                label="View"
                                component={RouterLink}
                                to={`/${business.url}`}
                                clickable
                                variant="outlined"
                              />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
                            <Chip
                              size="small"
                              color={statusColor[inv.status as InviteStatus] ?? "default"}
                              label={getInviteStatusLabel(inv.status as InviteStatus, residentReference)}
                            />
                            {inviteSentAt && (
                              <Chip size="small" variant="outlined" label={`Sent ${inviteSentAt}`} />
                            )}
                            {respondedAt && (
                              <Chip size="small" variant="outlined" label={respondedAt} />
                            )}
                          </Stack>
                        </Box>

                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{
                            pl: 1,
                            borderLeft: "1px solid",
                            borderColor: "divider",
                            minHeight: 40,
                          }}
                        >
                          {businessEmail ? (
                            <Tooltip title="Email provider">
                              <IconButton size="small" component="a" href={`mailto:${businessEmail}`}>
                                <EmailOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {businessPhone ? (
                            <Tooltip title="Call provider">
                              <IconButton size="small" component="a" href={`tel:${businessPhone}`}>
                                <PhoneOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {businessAddress ? (
                            <Tooltip title="Open address in Google Maps">
                              <IconButton
                                size="small"
                                component="a"
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessAddress)}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <LocationOnOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </Stack>

                      {inv.providerNote ? (
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          <strong>Note: </strong>{inv.providerNote}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No note from provider yet.
                        </Typography>
                      )}

                      {businessPhone ? (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Phone: </strong>{businessPhone}
                        </Typography>
                      ) : null}
                      {businessEmail ? (
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                          <strong>Email: </strong>{businessEmail}
                        </Typography>
                      ) : null}
                      {businessAddress ? (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Address: </strong>{businessAddress}
                        </Typography>
                      ) : null}
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      sx={{
                        mt: 2,
                        pt: 1.5,
                        borderTop: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      {canSendPrivateInvites && inv.status === "DRAFT" ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SendIcon />}
                          disabled={sendingDraftInvites}
                          onClick={() => onSendDraftInvite(inv.businessId ?? inv?.business?.id ?? null)}
                        >
                          Send to Provider
                        </Button>
                      ) : null}
                      {canResendPrivateInvite ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SendIcon />}
                          disabled={resendingInvite}
                          onClick={() => onResendInvite(inv.id)}
                        >
                          Resend Invite
                        </Button>
                      ) : null}
                      <Tooltip title={providerUser ? "" : "Providers only"}>
                        <span>
                          <Button
                            size="small"
                            variant="text"
                            color="error"
                            startIcon={<CancelIcon />}
                            disabled={!providerUser || !canProviderAct || responding || Boolean(winnerInviteId)}
                            onClick={() => onProviderRespond(inv.id, "DECLINED")}
                          >
                            Decline
                          </Button>
                        </span>
                      </Tooltip>

                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DoneAllIcon />}
                        disabled={!canPickThis || picking}
                        onClick={() => onPickWinner(inv.id)}
                      >
                        Pick Winner
                      </Button>
                    </Stack>
                  </Paper>
                );
              })
            )}
          </Box>
        </Paper>

        {/* Respond dialog for providers */}
        <RespondDialog
          open={Boolean(respondState.inviteId && respondState.action)}
          onClose={() => setRespondState({ inviteId: null, action: null })}
          onSubmit={actuallyRespond}
          title={
            respondState.action === "ACCEPTED" ? "Accept Invite" :
              respondState.action === "DECLINED" ? "Decline Invite" :
                "Respond to Invite"
          }
          actionLabel={
            respondState.action === "ACCEPTED" ? "Accept" :
              respondState.action === "DECLINED" ? "Decline" :
                "Submit"
          }
        />

        <Dialog
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Add Providers To This Batch</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Search for a provider by name, city, zip, or URL. You can add them to this same batch and optionally send the invite right away.
              </Alert>
              <TextField
                label="Search providers"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchActionError(null);
                }}
                placeholder="Example: Comstock, 99203, Spokane"
                fullWidth
              />
              {searchActionError ? (
                <Alert severity="error">{searchActionError}</Alert>
              ) : null}
              {searchError ? (
                <Alert severity="error">{searchError.message}</Alert>
              ) : null}
              {searchQuery.trim().length < 2 ? (
                <Typography variant="body2" color="text.secondary">
                  Type at least 2 characters to search.
                </Typography>
              ) : null}
              {searchingBusinesses || searchingFallbackBusinesses ? <LinearProgress /> : null}
              {searchQuery.trim().length >= 2 &&
              !searchError &&
              !searchingBusinesses &&
              !searchingFallbackBusinesses &&
              searchResults.length === 0 &&
              existingBatchSearchResults.length === 0 ? (
                <Alert severity="warning">
                  No providers matched that search.
                </Alert>
              ) : null}
              <Stack spacing={1.25}>
                {searchResults.map((business: any) => {
                  const phone = business?.phoneNumbers?.[0]?.phoneNumber || null;
                  const email = business?.emails?.[0] || null;
                  const address = formatBusinessAddress(business);
                  return (
                    <Paper key={business.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle1" fontWeight={700}>
                            {business?.name || "Provider"}
                          </Typography>
                          {business?.claimStatus ? (
                            <Chip size="small" variant="outlined" label={business.claimStatus} />
                          ) : null}
                        </Stack>
                        {address ? (
                          <Typography variant="body2" color="text.secondary">
                            {address}
                          </Typography>
                        ) : null}
                        {email || phone ? (
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            {phone ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Phone: ${phone}`}
                                component="a"
                                clickable
                                href={`tel:${phone}`}
                                sx={{ width: "fit-content" }}
                              />
                            ) : null}
                            {email ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Email: ${email}`}
                                component="a"
                                clickable
                                href={`mailto:${email}`}
                                sx={{ width: "fit-content" }}
                              />
                            ) : null}
                          </Stack>
                        ) : null}
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={addingBusinesses}
                            onClick={() => onAddBusinessToBatch(business.id, false)}
                          >
                            Add To Batch
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={addingBusinesses || sendingDraftInvites}
                            onClick={() => onAddBusinessToBatch(business.id, true)}
                          >
                            Add And Send Now
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
                {existingBatchSearchResults.length > 0 ? (
                  <Alert severity="info">
                    These providers matched your search and are already part of this batch.
                  </Alert>
                ) : null}
                {existingBatchSearchResults.map((business: any) => {
                  const phone = business?.phoneNumbers?.[0]?.phoneNumber || null;
                  const email = business?.emails?.[0] || null;
                  const address = formatBusinessAddress(business);
                  return (
                    <Paper
                      key={`existing-${business.id}`}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}
                    >
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle1" fontWeight={700}>
                            {business?.name || "Provider"}
                          </Typography>
                          <Chip size="small" color="info" variant="outlined" label="Already In Batch" />
                          {business?.claimStatus ? (
                            <Chip size="small" variant="outlined" label={business.claimStatus} />
                          ) : null}
                        </Stack>
                        {address ? (
                          <Typography variant="body2" color="text.secondary">
                            {address}
                          </Typography>
                        ) : null}
                        {email || phone ? (
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            {phone ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Phone: ${phone}`}
                                component="a"
                                clickable
                                href={`tel:${phone}`}
                                sx={{ width: "fit-content" }}
                              />
                            ) : null}
                            {email ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Email: ${email}`}
                                component="a"
                                clickable
                                href={`mailto:${email}`}
                                sx={{ width: "fit-content" }}
                              />
                            ) : null}
                          </Stack>
                        ) : null}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSearchOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!snack}
          autoHideDuration={3500}
          onClose={closeSnack}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <MUIAlert onClose={closeSnack} severity="success" variant="filled" elevation={6}>
            {snack}
          </MUIAlert>
        </Snackbar>
      </Container>
    </Box>
  );
}
